import { createEnv } from '@t3-oss/env-nextjs';
import { z } from 'zod';

function intoJson(value: string, context: z.RefinementCtx) {
  if (!value) return undefined;

  try {
    return JSON.parse(value);
  } catch {
    context.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Invalid JSON',
    });
    return z.never;
  }
}

function boolean() {
  return z
    .string()
    .optional()
    .transform((value) => value === 'true' || value === '1');
}

export const env = createEnv({
  server: {
    ROLLBAR_SERVER_TOKEN: z.string().optional(),
    FIREBASE_ADMIN_CREDENTIAL: z
      .string()
      .transform(intoJson)
      // Firebase type definition doesn't match the actual structure of the key provided,
      // we use any to avoid unnecessary conversions.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any, camelcase
      .pipe<any>(z.object({ private_key: z.string(), project_id: z.string() }).passthrough()),
    FIREBASE_PRIVATE_KEY: z.string().optional(),
    FIREBASE_AUTH_EMULATOR_HOST: z.string().optional(),
    FIRESTORE_EMULATOR_HOST: z.string().optional(),
    FIREBASE_STORAGE_EMULATOR_HOST: z.string().optional(),
    GA4_PROPERTY_ID: z.string().optional(),
    GA4_APP_STREAM_IDS: z.string().optional(),
    GA4_WEB_STREAM_IDS: z.string().optional(),
    SMTP_HOST: z.string(),
    SMTP_PORT: z.string().transform((value) => parseInt(value, 10)),
    SMTP_SECURE: z.string().transform((value) => value === 'true'),
    SMTP_USER: z.string(),
    SMTP_SENDER_NAME: z.string(),
    SMTP_SENDER_EMAIL: z.string(),
    SMTP_TOKEN: z.string(),
    SMTP_UNSUBSCRIBE_URL: z.string(),
    SMTP_TEST_EMAIL: z.string().optional(),
    OTC_RECIPIENT_EMAIL: z.string().email(),
    ENCRYPTION_KEY: z.string(),
    ENCRYPTION_IV: z.string(),
    AWESOME_API_BASE_URL: z.string().default('https://economia.awesomeapi.com.br/json'),
    BITSO_API_KEY: z.string().optional(),
    BITSO_API_SECRET: z.string().optional(),
    BITSO_BASE_URL: z.string().default('https://api.bitso.com/api/v3'),
    BLOCKRIVER_API_KEY: z.string().optional(),
    BLOCKRIVER_API_SECRET: z.string().optional(),
    BLOCKRIVER_BASE_URL: z.string().default('https://api.blockriver.com/api/v3'),
    TRADINGVIEW_BASE_URL: z.string().default('http://191.209.58.108:8091/api'),
    ENOR_API_KEY: z.string().optional(),
    ENOR_API_SECRET: z.string().optional(),
    ENOR_USERNAME: z.string().optional(),
    ENOR_PASSWORD: z.string().optional(),
    ENOR_SERVER_URL: z.string().default('wss://api.otc.test.enordigital.com/websocket'),
    GREENLABS_API_KEY: z.string().optional(),
    GREENLABS_API_SECRET: z.string().optional(),
    GREENLABS_API_BASE_URL: z.string().default('http://vps61343.publiccloud.com.br:8000'),
    EXCHANGE_NOW_API_BASE_URL: z.string().default('https://sandbox.b2bexchange.digital/api'),
    EXCHANGE_NOW_API_KEY: z.string().optional(),
    SMARTFIN_API_KEY: z.string().optional(),
    SMARTFIN_OPERATION_API_KEY: z.string().optional(),
    SMARTFIN_BASE_URL: z.string().default('https://smartfin.sistemaneosystem.com/api'),
    SMARTFIN_DEBUG: z
      .string()
      .optional()
      .transform((value) => value === 'true' || value === '1'),
    SHOW_INVALID_EMAIL_ERROR: z
      .string()
      .optional()
      .transform((value) => value === 'true' || value === '1'),
    COMPLIANCE_MAIL_ADDRESS: z.string().email(),
    SICOOB_ENVIRONMENT: z.enum(['sandbox', 'production']).default('sandbox'),
    SICOOB_PIX_BASE_URL: z.string().default('https://api.sicoob.com.br'),
    SICOOB_AUTH_BASE_URL: z.string().default('https://auth.sicoob.com.br'),
    SICOOB_CLIENT_ID: z.string().optional(),
    SICOOB_PFX_CERTIFICATE: z.string().optional(),
    SICOOB_PFX_PASSPHRASE: z.string().optional(),
    SICOOB_SANDBOX_TOKEN: z.string().optional(),
    SICOOB_DEFAULT_PIX_KEY: z.string().optional(),
    SICOOB_DEBUG: boolean(),
    ONLYUP_ACCOUNT_NAME: z.string().optional(),
    ONLYUP_PIX_BASE_URL: z.string().default('https://api.pix.onlyup.com.br'),
    ONLYUP_CLIENT_ID: z.string().optional(),
    ONLYUP_CLIENT_SECRET: z.string().optional(),
    ONLYUP_PFX_CERTIFICATE: z.string().optional(),
    ONLYUP_PFX_PASSPHRASE: z.string().optional(),
    ONLYUP_DEFAULT_PIX_KEY: z.string().optional(),
    ONLYUP_WEBHOOK_SECRET: z.string().optional(),
    ONLYUP_DEBUG: boolean(),
    PIX_PROVIDER: z.enum(['SICOOB', 'ONLYUP']).default('SICOOB'),
    CRON_SECRET: z.string().min(16),
    BIGDATACORP_BASE_URL: z.string().default('https://app.bigdatacorp.com.br'),
    BIGDATACORP_DEFAULT_GROUP: z.string().optional(),
    BIGDATACORP_ACCESS_TOKEN: z.string().optional(),
    BIGDATACORP_DEBUG: z
      .string()
      .optional()
      .transform((value) => value === 'true' || value === '1'),
    SANDBOX: boolean(),
    WEBHOOK_DEBUG: boolean(),
  },

  client: {
    NEXT_PUBLIC_ROLLBAR_CLIENT_TOKEN: z.string().optional(),
    NEXT_PUBLIC_ROLLBAR_TIME_WINDOW_MIN: z.number().optional(),
    NEXT_PUBLIC_ROLLBAR_ENV: z.string().optional(),
    NEXT_PUBLIC_FIREBASE_CONFIG: z.string().transform(intoJson).pipe(z.object({}).loose()),
    NEXT_PUBLIC_FIREBASE_MESSAGING_VAPID_KEY: z.string().min(1),
    NEXT_PUBLIC_FIREBASE_AUTH_EMULATOR_HOST: z.string().optional(),
    NEXT_PUBLIC_FIRESTORE_EMULATOR_HOST: z.string().optional(),
    NEXT_PUBLIC_FIREBASE_STORAGE_EMULATOR_HOST: z.string().optional(),
    NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA: z.string().optional(),
    NEXT_PUBLIC_URL: z.string().optional(),
    NEXT_PUBLIC_GA_TRACKING_ID: z.string().optional(),
    NEXT_PUBLIC_GOOGLE_ANALYTICS: z.string().optional(),
    NEXT_PUBLIC_FACEBOOK_PIXEL_ID: z.string().optional(),
    NEXT_PUBLIC_FORCE_SUBDOMAIN: z.string().optional(),
    NEXT_PUBLIC_API_URL: z.string().optional(),
    NEXT_PUBLIC_API_REST_BASE_URL: z.string().optional(),
    NEXT_PUBLIC_CLOUDINARY_UPLOAD_PRESET: z.string().optional(),
    NEXT_PUBLIC_CLOUDINARY_CLOUD_NAME: z.string().optional(),
    NEXT_PUBLIC_BRAND: z.enum(['sttart', 'start', 'prime', 'prime-shop']).default('start'),
  },

  runtimeEnv: {
    ROLLBAR_SERVER_TOKEN: process.env.ROLLBAR_SERVER_TOKEN,
    FIREBASE_ADMIN_CREDENTIAL: process.env.FIREBASE_ADMIN_CREDENTIAL,
    FIREBASE_PRIVATE_KEY: process.env.FIREBASE_PRIVATE_KEY,
    FIREBASE_AUTH_EMULATOR_HOST: process.env.FIREBASE_AUTH_EMULATOR_HOST,
    FIRESTORE_EMULATOR_HOST: process.env.FIRESTORE_EMULATOR_HOST,
    FIREBASE_STORAGE_EMULATOR_HOST: process.env.FIREBASE_STORAGE_EMULATOR_HOST,
    GA4_PROPERTY_ID: process.env.GA4_PROPERTY_ID,
    GA4_APP_STREAM_IDS: process.env.GA4_APP_STREAM_IDS,
    GA4_WEB_STREAM_IDS: process.env.GA4_WEB_STREAM_IDS,
    SMTP_HOST: process.env.SMTP_HOST,
    SMTP_PORT: process.env.SMTP_PORT,
    SMTP_SECURE: process.env.SMTP_SECURE,
    SMTP_USER: process.env.SMTP_USER,
    SMTP_SENDER_NAME: process.env.SMTP_SENDER_NAME,
    SMTP_SENDER_EMAIL: process.env.SMTP_SENDER_EMAIL,
    SMTP_TOKEN: process.env.SMTP_TOKEN,
    SMTP_UNSUBSCRIBE_URL: process.env.SMTP_UNSUBSCRIBE_URL,
    SMTP_TEST_EMAIL: process.env.SMTP_TEST_EMAIL,
    OTC_RECIPIENT_EMAIL: process.env.OTC_RECIPIENT_EMAIL,
    ENCRYPTION_KEY: process.env.ENCRYPTION_KEY,
    ENCRYPTION_IV: process.env.ENCRYPTION_IV,
    AWESOME_API_BASE_URL: process.env.AWESOME_API_BASE_URL,
    BITSO_API_KEY: process.env.BITSO_API_KEY,
    BITSO_API_SECRET: process.env.BITSO_API_SECRET,
    BITSO_BASE_URL: process.env.BITSO_BASE_URL,
    BLOCKRIVER_API_KEY: process.env.BLOCKRIVER_API_KEY,
    BLOCKRIVER_API_SECRET: process.env.BLOCKRIVER_API_SECRET,
    BLOCKRIVER_BASE_URL: process.env.BLOCKRIVER_BASE_URL,
    TRADINGVIEW_BASE_URL: process.env.TRADINGVIEW_BASE_URL,
    ENOR_API_KEY: process.env.ENOR_API_KEY,
    ENOR_API_SECRET: process.env.ENOR_API_SECRET,
    ENOR_USERNAME: process.env.ENOR_USERNAME,
    ENOR_PASSWORD: process.env.ENOR_PASSWORD,
    ENOR_SERVER_URL: process.env.ENOR_SERVER_URL,
    GREENLABS_API_KEY: process.env.GREENLABS_API_KEY,
    GREENLABS_API_SECRET: process.env.GREENLABS_API_SECRET,
    GREENLABS_API_BASE_URL: process.env.GREENLABS_API_BASE_URL,
    EXCHANGE_NOW_API_BASE_URL: process.env.EXCHANGE_NOW_API_BASE_URL,
    EXCHANGE_NOW_API_KEY: process.env.EXCHANGE_NOW_API_KEY,
    SMARTFIN_API_KEY: process.env.SMARTFIN_API_KEY,
    SMARTFIN_OPERATION_API_KEY: process.env.SMARTFIN_OPERATION_API_KEY,
    SMARTFIN_BASE_URL: process.env.SMARTFIN_BASE_URL,
    SMARTFIN_DEBUG: process.env.SMARTFIN_DEBUG,
    SHOW_INVALID_EMAIL_ERROR: process.env.SHOW_INVALID_EMAIL_ERROR,
    COMPLIANCE_MAIL_ADDRESS: process.env.COMPLIANCE_MAIL_ADDRESS,
    SICOOB_CLIENT_ID: process.env.SICOOB_CLIENT_ID,
    SICOOB_AUTH_BASE_URL: process.env.SICOOB_AUTH_BASE_URL,
    SICOOB_PIX_BASE_URL: process.env.SICOOB_PIX_BASE_URL,
    SICOOB_PFX_CERTIFICATE: process.env.SICOOB_PFX_CERTIFICATE,
    SICOOB_PFX_PASSPHRASE: process.env.SICOOB_PFX_PASSPHRASE,
    SICOOB_SANDBOX_TOKEN: process.env.SICOOB_SANDBOX_TOKEN,
    SICOOB_ENVIRONMENT: process.env.SICOOB_ENVIRONMENT,
    SICOOB_DEFAULT_PIX_KEY: process.env.SICOOB_DEFAULT_PIX_KEY,
    SICOOB_DEBUG: process.env.SICOOB_DEBUG,
    ONLYUP_ACCOUNT_NAME: process.env.ONLYUP_ACCOUNT_NAME,
    ONLYUP_CLIENT_ID: process.env.ONLYUP_CLIENT_ID,
    ONLYUP_CLIENT_SECRET: process.env.ONLYUP_CLIENT_SECRET,
    ONLYUP_PIX_BASE_URL: process.env.ONLYUP_PIX_BASE_URL,
    ONLYUP_PFX_CERTIFICATE: process.env.ONLYUP_PFX_CERTIFICATE,
    ONLYUP_PFX_PASSPHRASE: process.env.ONLYUP_PFX_PASSPHRASE,
    ONLYUP_DEFAULT_PIX_KEY: process.env.ONLYUP_DEFAULT_PIX_KEY,
    ONLYUP_WEBHOOK_SECRET: process.env.ONLYUP_WEBHOOK_SECRET,
    ONLYUP_DEBUG: process.env.ONLYUP_DEBUG,
    PIX_PROVIDER: process.env.PIX_PROVIDER,
    CRON_SECRET: process.env.CRON_SECRET,
    BIGDATACORP_BASE_URL: process.env.BIGDATACORP_BASE_URL,
    BIGDATACORP_DEFAULT_GROUP: process.env.BIGDATACORP_DEFAULT_GROUP,
    BIGDATACORP_ACCESS_TOKEN: process.env.BIGDATACORP_ACCESS_TOKEN,
    BIGDATACORP_DEBUG: process.env.BIGDATACORP_DEBUG,
    SANDBOX: process.env.SANDBOX,
    WEBHOOK_DEBUG: process.env.WEBHOOK_DEBUG,

    NEXT_PUBLIC_ROLLBAR_CLIENT_TOKEN: process.env.NEXT_PUBLIC_ROLLBAR_CLIENT_TOKEN,
    NEXT_PUBLIC_ROLLBAR_TIME_WINDOW_MIN: +(process.env.NEXT_PUBLIC_TIME_WINDOW_MIN || 0),
    NEXT_PUBLIC_ROLLBAR_ENV: process.env.NEXT_PUBLIC_ROLLBAR_ENV,
    NEXT_PUBLIC_FIREBASE_CONFIG: process.env.NEXT_PUBLIC_FIREBASE_CONFIG,
    NEXT_PUBLIC_FIREBASE_MESSAGING_VAPID_KEY: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_VAPID_KEY,
    NEXT_PUBLIC_FIREBASE_AUTH_EMULATOR_HOST: process.env.NEXT_PUBLIC_FIREBASE_AUTH_EMULATOR_HOST,
    NEXT_PUBLIC_FIRESTORE_EMULATOR_HOST: process.env.NEXT_PUBLIC_FIRESTORE_EMULATOR_HOST,
    NEXT_PUBLIC_FIREBASE_STORAGE_EMULATOR_HOST:
      process.env.NEXT_PUBLIC_FIREBASE_STORAGE_EMULATOR_HOST,
    NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA: process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA,
    NEXT_PUBLIC_URL: process.env.NEXT_PUBLIC_URL,
    NEXT_PUBLIC_GA_TRACKING_ID: process.env.NEXT_PUBLIC_GA_TRACKING_ID,
    NEXT_PUBLIC_GOOGLE_ANALYTICS: process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS,
    NEXT_PUBLIC_FACEBOOK_PIXEL_ID: process.env.NEXT_PUBLIC_FACEBOOK_PIXEL_ID,
    NEXT_PUBLIC_FORCE_SUBDOMAIN: process.env.NEXT_PUBLIC_FORCE_SUBDOMAIN,
    NEXT_PUBLIC_API_URL: process.env.NEXT_PUBLIC_API_URL,
    NEXT_PUBLIC_API_REST_BASE_URL: process.env.NEXT_PUBLIC_API_REST_BASE_URL,
    NEXT_PUBLIC_CLOUDINARY_UPLOAD_PRESET: process.env.NEXT_PUBLIC_CLOUDINARY_UPLOAD_PRESET,
    NEXT_PUBLIC_CLOUDINARY_CLOUD_NAME: process.env.NEXT_PUBLIC_CLOUDINARY_CLOUD_NAME,
    NEXT_PUBLIC_BRAND: process.env.NEXT_PUBLIC_BRAND,
  },
});

export type Environment = typeof env;
